import { Injectable } from '@angular/core';
import { throwError as observableThrowError, Observable, throwError } from 'rxjs';
import { OperationResult } from 'src/app/commons/operation-result';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators/catchError';
import { environment } from 'src/environments/environment';
import { AccountRole } from 'src/app/model/account-role';
import { AuthorizationProperties } from 'src/app/authentication/auth-info';
import { LoginService } from './login.service';
import { UpdateRolesInfo } from 'src/app/model/update-roles';
import { WorkFlowService } from './work-flow.service';
import { TranslatedVariables } from 'src/app/commons/translated-variables';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { RoleNumber } from 'src/app/model/role-number';
import { UrlMaker } from 'src/app/commons/url-maker';
import { RolesWeight } from 'src/app/authentication/auth-helpers';
import { StoreRolesService } from './store-role.service';

@Injectable({ providedIn: 'root' })
export class RolesService {
    public authorizationStored;
    public authorizationProperties;
    isSessionStorage: boolean;
    storeApiUrl: string;
    StoreRoles: string[] = [];
    public hasImpExpAdminRole = false;
    public hasAdminRole = false;
    public hasAmazonDSPAccountRole = false;
    public hasDevOpsOrHigher = false;

    constructor(
        private http: HttpClient,
        public loginService: LoginService,
        private workflow: WorkFlowService,
        private variable: TranslatedVariables,
        private storeRoleService: StoreRolesService
    ) {
        this.storeApiUrl = environment.settings.storeApiUrl;
        this.isSessionStorage = environment.auth.sessionStorage;
    }

    getRolesBy(accountName: string): Observable<OperationResult> {
        if (accountName === '') {
            return observableThrowError('The model name to query is missing!');
        }

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('accountroles').appendSegment(accountName).toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => ErrorObservable.create(error || 'Server error')));
    }

    async getRolesToPromiseBy(accountName: string): Promise<AccountRole[]> {
        if (accountName === '') {
            console.log('Account name is missing!');
            return;
        }

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        this.storeApiUrl = environment.settings.storeApiUrl;
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('accountroles').appendSegment(accountName).toString();

        return this.http
            .get<AccountRole[]>(url, { headers: headers })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    let errorDetail = error.message;

                    if (error.status === 401) {
                        return throwError('Unauthorized: ' + errorDetail);
                    }
                    if (error.status === 403) {
                        return throwError('Forbidden resource: ' + errorDetail);
                    }

                    return throwError(errorDetail || 'server error (getRolesToPromiseBy)');
                })
            )
            .toPromise();
    }

    GetListRolesToAssociate(accountName: string, roleLevel: string, subKey: string): Observable<OperationResult> {
        this.storeApiUrl = environment.settings.storeApiUrl;
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('listrolestoassociate')
            .appendSegment(accountName)
            .appendSegment(roleLevel)
            .appendSegment(subKey)
            .toString();

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in getRolesToAssociate')));
    }

    updateRoleAssociation(body: UpdateRolesInfo): Observable<OperationResult> {
        let bodyString = JSON.stringify(body);
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('updateroles').toString();

        return this.http
            .post<OperationResult>(url, bodyString, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => ErrorObservable.create(error || 'Server error in updateRoleAssociation')));
    }

    checkRolesMin(rolelevelMin: number, includeApplicativeRoles: boolean = false): boolean {
        if (this.authorizationProperties) {
            let accountRoles = this.authorizationProperties.roles as AccountRole[];
            if (accountRoles.length === 0) {
                return false;
            }
            if (!includeApplicativeRoles) {
                accountRoles = accountRoles.filter((x) => x.Weight < 100); // escludi i ruoli applicativi con peso > 100)
            }
            return accountRoles.some((x) => x.Weight >= rolelevelMin);
        }
        return false;
    }

    checkSpecificRole(roleName: number): boolean {
        if (this.authorizationProperties) {
            let accountRoles = this.authorizationProperties.roles as AccountRole[];
            if (accountRoles.length === 0) {
                return false;
            }
            return accountRoles.some((x) => x.Weight === roleName);
        }
        return false;
    }

    async refreshRoles(AccountName: string) {
        let resultRoles = await this.getRolesToPromiseBy(AccountName);
        if (!resultRoles || !resultRoles['Result']) {
            this.workflow.showMessage(this.variable.errorTitle, resultRoles['Message']);
            return;
        }
        let accountRoles: AccountRole[] = resultRoles['Content'];
        if (accountRoles.length === 0) {
            return;
        }
        this.authorizationProperties.roles = accountRoles as AccountRole[];
        this.authorizationProperties.roles.forEach((x) => (x.RoleName = x.RoleName.split('-').join('')));
        this.authorizationProperties.roles.forEach((x) => (x.Weight = RolesWeight[x.RoleName]));
        this.authorizationStored = JSON.stringify(this.authorizationProperties);
        this.hasImpExpAdminRole = this.checkSpecificRole(RolesWeight.APPLICATIONADMIN);
        this.hasAdminRole = this.checkRolesMin(RolesWeight.COUSERADMIN);
        this.hasAmazonDSPAccountRole = this.checkSpecificRole(RolesWeight.AMAZONDSP);
        this.hasDevOpsOrHigher = this.checkRolesMin(RolesWeight.DEVOPS);
        //cerco i ruoli dello store
        await this.storeRoleService.saveSTRolesInSession(this.authorizationProperties.accountName);
        // if (accountRoles.some((x) => x.Weight === 6 && x.EntityKey === 'purchased subscription')) {
        // TODO inserire nella condizione sottostante un contrllo che verifichi la subscription appena comprata
        if (environment.auth.sessionStorage) {
            sessionStorage.setItem('M4_account_roles', JSON.stringify(this.authorizationProperties.roles));
            sessionStorage.setItem('auth-info', this.authorizationStored);
        } else {
            localStorage.setItem('M4_account_roles', JSON.stringify(this.authorizationProperties.roles));
            localStorage.setItem('auth-info', this.authorizationStored);
        }
    }
    clear() {
        this.authorizationStored = null;
        this.authorizationProperties = null;
        this.isSessionStorage = false;
        this.storeApiUrl = '';
        this.StoreRoles = [];
        this.hasImpExpAdminRole = false;
        this.hasAdminRole = false;
        this.hasAmazonDSPAccountRole = false;
        this.hasDevOpsOrHigher = false;
    }
}
